import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';

const AuthContext = createContext();

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    // Initialize auth state on app start
    initializeAuth();
  }, []);

  const initializeAuth = async () => {
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      setToken(storedToken);
      await loadUser();
    } else {
      setLoading(false);
    }
  };

  const loadUser = async () => {
    try {
      const userData = await api.getCurrentUser();
      setUser(userData);
    } catch (error) {
      console.error('Failed to load user:', error);
      // Only logout if it's a 401 error (invalid token)
      // For other errors (network, server), keep the token and let user retry
      if (error.response?.status === 401) {
        logout();
      } else {
        // For other errors, just set user to null but keep token
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, totpToken = null) => {
    const response = await api.login(email, password, totpToken);
    
    if (response.requiresTOTP) {
      return { requiresTOTP: true };
    }

    const { token: authToken, user: userData } = response;
    localStorage.setItem('token', authToken);
    setToken(authToken);
    setUser(userData);
    
    return { success: true };
  };

  const register = async (token, password) => {
    const response = await api.register(token, password);
    const { token: authToken, user: userData } = response;
    
    localStorage.setItem('token', authToken);
    setToken(authToken);
    setUser(userData);
    
    return { success: true };
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    reloadUser: loadUser,
    refreshAuth: initializeAuth
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

